import { MrfiktivHttpClientProvider } from "@/services/mrfiktiv/mrfiktiv-http-client.provider";
import { Company } from "../v1/Company";
import {
  MrfiktivCompanyControllerFindByGeocodeParamsGen,
  MrfiktivCompanyControllerGetParamsGen,
  MrfiktivCompanyMatchViewModelGen,
  MrfiktivCreateCompanyDtoGen,
  MrfiktivMatchCompanyDtoGen,
  MrfiktivMultipleIdsDtoGen,
  MrfiktivUpdateCompanyDtoGen
} from "../v1/data-contracts";

/**
 * Communicate with company API
 */
class CompanyService {
  mrfiktivProxy: Company;

  /**
   * Construct service
   */
  constructor(mrfiktivHttpClientProvider: MrfiktivHttpClientProvider) {
    this.mrfiktivProxy = new Company(mrfiktivHttpClientProvider.client());
  }

  /**
   * Create an company
   */
  async create(partnerId: string, data: MrfiktivCreateCompanyDtoGen) {
    const company = (await this.mrfiktivProxy.companyControllerCreate(partnerId, data)).data;

    return company;
  }

  /**
   * Get all company
   */
  async get(data: MrfiktivCompanyControllerGetParamsGen) {
    const companies = (await this.mrfiktivProxy.companyControllerGet(data)).data;

    return companies;
  }

  /**
   * Get all company
   */
  async getMany(partnerId: string, data: MrfiktivMultipleIdsDtoGen) {
    const companies = (await this.mrfiktivProxy.companyControllerGetMany(partnerId, data)).data;

    return companies;
  }

  /**
   * Find the companies that most likely are the given party, best first
   */
  async match(partnerId: string, data: MrfiktivMatchCompanyDtoGen): Promise<MrfiktivCompanyMatchViewModelGen[]> {
    const matches = (await this.mrfiktivProxy.companyControllerMatch(partnerId, data)).data;

    return matches;
  }

  /**
   * Get a company
   */
  async getOne(partnerId: string, companyId: string) {
    const company = (await this.mrfiktivProxy.companyControllerGetOne(partnerId, companyId)).data;

    return company;
  }

  /**
   * Update a company
   */
  async update(partnerId: string, companyId: string, data: MrfiktivUpdateCompanyDtoGen) {
    const company = (await this.mrfiktivProxy.companyControllerUpdate(partnerId, companyId, data)).data;

    return company;
  }

  /**
   * Delete a company
   */
  async delete(partnerId: string, companyId: string) {
    const company = (await this.mrfiktivProxy.companyControllerDelete(partnerId, companyId)).data;

    return company;
  }

  /**
   * Mark a company as the partner's default company
   */
  async markIsPartner(partnerId: string, companyId: string) {
    const company = (await this.mrfiktivProxy.companyControllerMarkIsPartner(partnerId, companyId)).data;

    return company;
  }

  /**
   * Delete a company
   */
  async findByGeocode(query: MrfiktivCompanyControllerFindByGeocodeParamsGen) {
    const company = (await this.mrfiktivProxy.companyControllerFindByGeocode(query)).data;

    return company;
  }

  /**
   * Get groups of companies that share the same normalized tax number
   */
  async getDuplicateTaxnumbers(partnerId: string) {
    const duplicates = (await this.mrfiktivProxy.companyControllerGetDuplicateTaxnumbers(partnerId)).data;

    return duplicates;
  }

  /**
   * Recompute the normalized tax number of all companies of a partner
   */
  async regenerateTaxnumberNormalized(partnerId: string) {
    const result = (await this.mrfiktivProxy.companyControllerRegenerateTaxnumberNormalized(partnerId)).data;

    return result;
  }
}

/**
 * Communicate with company API
 */
export default new CompanyService(new MrfiktivHttpClientProvider());
